import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Share2, Menu, LogOut, MessageCircle, User, Shield, Building2, Link } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useBranding } from "@/hooks/useBranding";
import DefaultLogo from "@/assets/golfrecruit-lockup-light.svg";
import BrandMark from "@/assets/golfrecruit-mark.svg";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { companyEmail } from "@/constants/domains";
import { formatEvidence } from "@/components/pipeline/pipelineDesign";

interface ChatHeaderProps {
  playerName: string;
  jgsRank: string;
  class_rank: string;
  class_year: string;
  profileGradYear?: string;
  scoringDifferential: string;
  isLinked: boolean;
  onOpenSidebar?: () => void;
  onShare?: () => void;
  onLogout?: () => void;
  userType?: "player" | "coach";
  onLinkProfile?: () => void;
}

function formatHeaderDifferential(value: string): string {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? formatEvidence(parsed) : value;
}

const ChatHeader = ({
  playerName,
  jgsRank,
  class_rank,
  class_year,
  profileGradYear,
  scoringDifferential,
  isLinked,
  onOpenSidebar,
  onShare,
  onLogout,
  userType,
  onLinkProfile,
}: ChatHeaderProps) => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const logoSrc = branding?.headerLogoUrl || DefaultLogo;

  // Detect elevated roles via sessionStorage
  let hasAdminToken = false;
  let hasCustomerToken = false;
  try {
    const role = sessionStorage.getItem("recruit_ai_user_role");
    hasAdminToken = role === "admin";
    hasCustomerToken = role === "admin" || role === "customer";
  } catch {}
  return (
    <header className="bg-card border-b border-border px-4 py-3 md:px-6 md:py-4 flex items-center justify-between gap-3 md:gap-6">
      <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onOpenSidebar}
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <img
          src={logoSrc}
          alt="golfrecruit.ai"
          className="hidden sm:block h-8 w-auto mr-3 md:mr-4"
        />
        {/* Phones have no room for the wordmark: show our mark, unless an academy brand is set */}
        {!branding?.headerLogoUrl && (
          <img src={BrandMark} alt="" className="h-8 w-8 shrink-0 sm:hidden" />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-xl font-semibold text-foreground truncate">
              {isLinked ? playerName : playerName !== "Player Name" ? playerName : "Player Name"}
            </h1>
            {isLinked ? (
              <span className="text-xs md:text-sm font-medium text-muted-foreground whitespace-nowrap">
                Class {class_year || "N/A"}
              </span>
            ) : null}
          </div>
          <p className="text-xs md:text-sm text-muted-foreground truncate">
            {isLinked ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help border-b border-dotted border-muted-foreground/40">
                      Class rank {/^\d+$/.test(class_rank) ? `#${class_rank}` : class_rank}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Your ranking among all JGS-ranked players in the class of {class_year}
                  </TooltipContent>
                </Tooltip>
                {" · "}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help border-b border-dotted border-muted-foreground/40">
                      Differential {formatHeaderDifferential(scoringDifferential)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Your scoring differential in JGS ranked tournament play — the lower, the better.
                    This measures strokes over par adjusted for course difficulty.
                  </TooltipContent>
                </Tooltip>
              </>
            ) : playerName !== "Player Name" && profileGradYear ? (
              <>Class of {profileGradYear} · Building your recruiting profile</>
            ) : (
              <>
                <button onClick={onLinkProfile} className="text-primary-text hover:underline">
                  Link your JGS profile
                </button>
                <span className="hidden sm:inline">
                  {" for personalized school recommendations"}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {onShare && (
          <>
            {/* mobile-only share icon */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label="Share"
              onClick={onShare}
            >
              <Share2 className="h-5 w-5" />
            </Button>
            {/* desktop share action */}
            <div className="hidden md:flex">
              <Button variant="outline" size="sm" onClick={onShare} className="mr-2">
                <Share2 className="mr-2 h-4 w-4" />
                Share
              </Button>
            </div>
          </>
        )}
        {/* Avatar dropdown for account actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="User menu"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback>
                  <User className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {hasAdminToken && (
              <DropdownMenuItem
                onClick={() => {
                  navigate("/recruit-admin/dashboard");
                }}
              >
                <Shield className="mr-2 h-4 w-4" />
                <span>Admin Dashboard</span>
              </DropdownMenuItem>
            )}
            {hasCustomerToken && (
              <DropdownMenuItem
                onClick={() => {
                  navigate("/customer/dashboard");
                }}
              >
                <Building2 className="mr-2 h-4 w-4" />
                <span>Customer Dashboard</span>
              </DropdownMenuItem>
            )}
            {(hasAdminToken || hasCustomerToken) && <DropdownMenuSeparator />}
            {!isLinked && userType !== "coach" && onLinkProfile && (
              <DropdownMenuItem onClick={onLinkProfile}>
                <Link className="mr-2 h-4 w-4" />
                <span>Link JGS Profile</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                window.location.href = `mailto:${companyEmail("feedback")}`;
              }}
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              <span>Provide Feedback</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default ChatHeader;
